import { useTranslation } from 'react-i18next'
import { ChevronRight } from 'lucide-react'
import type { Match, Team } from '@brochure/shared'
import { LocalizedLink } from '@/components/LocalizedLink'
import { SideMark } from './SideMark'
import { cn } from '@/lib/utils'
import { ScoreFlaps } from './ScoreFlaps'
import { ScrollingName } from '@/components/ui/ScrollingName'

type MatchCardProps = {
  match: Match
  teams: Team[]
  tournamentId: string
  /** `night` nella striscia "In campo ora": riga d'inchiostro come il tabellone della home. */
  tone?: 'paper' | 'night'
  /** Falso per le copie (striscia live): il `data-testid` resta unico per partita. */
  withTestId?: boolean
  /** Numero d'ordine della partita nel girone, dentro la riga a sinistra. */
  number?: number
}

/**
 * Riga di una partita (casa, punteggio, trasferta): il punteggio sta in una casella d'inchiostro di larghezza fissa,
 * così le righe restano allineate. Toccabile verso la pagina partita solo se in corso o conclusa; la partita live
 * ha il bordo bordeaux e il pallino.
 */
export function MatchCard({ match, teams, tournamentId, tone = 'paper', withTestId = true, number }: MatchCardProps) {
  const { t } = useTranslation()
  const nameOf = (teamId: string) => teams.find((team) => team.id === teamId)?.name ?? t('zambePong.bracket.tbd')
  const hasScore = match.homeScore !== null && match.awayScore !== null
  const isLive = match.status === 'live'
  const homeWon = match.status === 'completed' && hasScore && match.homeScore! > match.awayScore!
  const awayWon = match.status === 'completed' && hasScore && match.awayScore! > match.homeScore!
  const clickable = match.status === 'live' || match.status === 'completed'
  const isNight = tone === 'night'

  const nameClass = (won: boolean) =>
    cn('min-w-0 flex-1 font-body text-[0.95rem] sm:text-base', won ? 'font-semibold' : '', isNight ? 'text-paper' : won || isLive ? 'text-ink' : 'text-ink-soft')

  // Sotto `sm` casa e trasferta stanno su due righe con il punteggio a destra (i nomi non si troncano);
  // da `sm` in su una riga sola, casa a sinistra del punteggio e trasferta a destra.
  const content = (
    <>
      <span
        className={cn(
          'col-start-1 row-span-2 self-stretch border-r pr-2 text-center font-body text-xs font-semibold tabular-nums sm:row-span-1 sm:self-center',
          'flex items-center justify-center',
          isNight ? 'border-paper/15 text-paper/60' : 'border-line text-ink-soft',
          number === undefined && 'invisible border-r-0 pr-0',
        )}
      >
        {number}
      </span>
      <span className="col-start-2 row-start-1 flex min-w-0 items-center gap-2.5">
        <SideMark side="home" />
        <ScrollingName className={cn(nameClass(homeWon), 'text-left sm:text-right')}>{nameOf(match.homeTeamId)}</ScrollingName>
      </span>
      <span className="relative col-start-3 row-span-2 row-start-1 flex w-[5.25rem] shrink-0 flex-col items-center justify-center self-center rounded-2xl bg-ink py-2 text-paper sm:row-span-1">
        {isLive && (
          <span className="absolute -top-2 flex items-center gap-1 rounded-full bg-coral px-2 py-px font-body text-[0.7rem] font-semibold text-paper">
            <span className="size-1 rounded-full bg-paper motion-safe:animate-pulse" aria-hidden="true" />
            {t('zambePong.live')}
          </span>
        )}
        <ScoreFlaps home={hasScore ? match.homeScore : null} away={hasScore ? match.awayScore : null} className="text-lg" />
      </span>
      <span className="col-start-2 row-start-2 flex min-w-0 items-center gap-2.5 sm:col-start-4 sm:row-start-1">
        <SideMark side="away" className="sm:order-last" />
        <ScrollingName className={cn(nameClass(awayWon), 'text-left')}>{nameOf(match.awayTeamId)}</ScrollingName>
      </span>
      <span className="col-start-4 row-span-2 row-start-1 flex items-center sm:col-start-5 sm:row-span-1">
        {clickable ? (
          <ChevronRight aria-hidden="true" className={cn('size-4 shrink-0', isNight ? 'text-paper/50' : 'text-ink-soft')} />
        ) : (
          <span aria-hidden="true" className="size-4 shrink-0" />
        )}
      </span>
    </>
  )

  const className = cn(
    'grid min-h-14 grid-cols-[1.5rem_minmax(0,1fr)_auto_auto] items-center gap-x-2.5 gap-y-1.5 rounded-2xl border px-3 py-2.5',
    'sm:grid-cols-[1.5rem_minmax(0,1fr)_auto_minmax(0,1fr)_auto] sm:gap-y-0',
    number === undefined && 'grid-cols-[0_minmax(0,1fr)_auto_auto] sm:grid-cols-[0_minmax(0,1fr)_auto_minmax(0,1fr)_auto]',
    isNight ? 'border-paper/15 bg-paper/5' : 'border-line bg-paper',
    isLive && !isNight && 'border-coral/60 shadow-card',
  )
  const testId = withTestId ? `match-${match.id}` : undefined

  if (clickable) {
    return (
      <LocalizedLink
        to="zambePongMatch"
        params={{ id: tournamentId, matchId: match.id }}
        data-testid={testId}
        className={cn(className, 'transition-[transform,border-color] duration-200 ease-out-expo active:scale-[0.99]', isNight ? 'hover:border-sun/50' : 'hover:border-sun')}
      >
        {content}
      </LocalizedLink>
    )
  }
  return (
    <div data-testid={testId} className={className}>
      {content}
    </div>
  )
}
